import BazarButton from './BazarButton';
import FlexBox from './FlexBox';
import PriceStack from './PriceStack';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Remove from '@mui/icons-material/Remove';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import React, { useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import shoppingBag from 'icons/shopping-bag.svg';
import { GlobalContext } from '../context/GlobalState';
import { routerUrls } from 'constants';
import { getTuckAllowanceNote, hasTuckAllowance } from '../utils/heightUtils';
import { strings } from 'localisation';
import PropTypes from 'prop-types';
import { cartLineKey } from '../utils/cartUtils';

// Square stepper button, shared by the − and + controls. A little tighter than
// the cart page's 36px, to suit the narrow drawer.
const stepperSx = {
  height: '32px',
  width: '32px',
  minWidth: '32px',
  p: 0,
  borderRadius: 2,
  borderColor: 'divider',
  color: 'text.secondary',
  '&:hover': {
    borderColor: 'gold.fill',
    backgroundColor: 'background.paper',
    color: 'text.primary'
  }
};

const MiniCart = ({ toggleSidenav }) => {
  const { cartList, changeCartItemQty, lang } = useContext(GlobalContext);

  const handleCartAmountChange = useCallback(
    (amount, product) => () => changeCartItemQty({ ...product, quantity: amount }),
    [changeCartItemQty]
  );

  const getTotalPrice = () =>
    cartList.reduce((acc, item) => acc + parseFloat(item.price) * Number(item.quantity), 0) || 0;

  const itemCount = cartList.length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: { xs: '86vw', sm: '400px' },
        maxWidth: '100%',
        bgcolor: 'background.default'
      }}>
      {/* ── header ─────────────────────────────────────────────── */}
      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2.25, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Box>
          <Typography variant="overline" sx={{ display: 'block', color: 'gold.text' }}>
            Кошница
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
            {itemCount} {itemCount === 1 ? strings[lang].product : strings[lang].products}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Затвори кошницата"
          onClick={toggleSidenav}
          sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
          <Close fontSize="small" />
        </IconButton>
      </FlexBox>

      {/* ── items ──────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {!itemCount && (
          <FlexBox
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', px: 4 }}>
            <Box component="img" src={shoppingBag} alt="" width={90} height={100} />
            <Typography
              sx={{ mt: 2.5, color: 'text.secondary', textAlign: 'center', fontSize: 14 }}>
              {strings[lang].emptyCartMessage}
            </Typography>
          </FlexBox>
        )}

        {cartList.map((item) => {
          const qty = Number(item.quantity);
          const lineEUR = parseFloat(item.price) * qty;

          return (
            <Box
              key={cartLineKey(item)}
              sx={{
                display: 'flex',
                gap: 1.75,
                px: 2.5,
                py: 2.25,
                borderBottom: '1px solid',
                borderColor: 'hairline.main'
              }}>
              <Box
                component={Link}
                to={`${routerUrls.product}/${item.slug_bg}/${item.id}`}
                onClick={toggleSidenav}
                sx={{ flexShrink: 0, lineHeight: 0 }}>
                <Box
                  component="img"
                  src={item.imgUrl}
                  alt={item.name}
                  sx={{
                    width: 76,
                    height: 76,
                    objectFit: 'cover',
                    borderRadius: '3px',
                    border: '1px solid',
                    borderColor: 'hairline.main'
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FlexBox justifyContent="space-between" alignItems="flex-start" sx={{ gap: 1 }}>
                  <Box
                    component={Link}
                    to={`${routerUrls.product}/${item.slug_bg}/${item.id}`}
                    onClick={toggleSidenav}
                    sx={{
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      '&:hover': { color: 'gold.deep' }
                    }}>
                    {item.name}
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={`Премахни ${item.name}`}
                    onClick={handleCartAmountChange(0, item)}
                    sx={{
                      p: '2px',
                      mt: '-2px',
                      color: 'text.disabled',
                      '&:hover': { color: 'text.primary' }
                    }}>
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </FlexBox>

                {item.attributes &&
                  Object.keys(item.attributes).map((attribute) => (
                    <Typography
                      key={item.attributes[attribute].id}
                      sx={{ color: 'text.secondary', fontSize: 12, mt: 0.5 }}>
                      {item.attributes[attribute].label}: {item.attributes[attribute].value} cm
                    </Typography>
                  ))}

                {hasTuckAllowance(item) && (
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 0.5 }}>
                    {getTuckAllowanceNote(lang)}
                  </Typography>
                )}

                <FlexBox alignItems="flex-end" justifyContent="space-between" sx={{ mt: 1.5 }}>
                  <FlexBox alignItems="center" sx={{ gap: 1 }}>
                    <BazarButton
                      variant="outlined"
                      disabled={qty === 1}
                      aria-label="Намали количеството"
                      sx={stepperSx}
                      onClick={handleCartAmountChange(qty - 1, item)}>
                      <Remove sx={{ fontSize: 16 }} />
                    </BazarButton>
                    <Box
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        minWidth: 18,
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums'
                      }}>
                      {qty}
                    </Box>
                    <BazarButton
                      variant="outlined"
                      aria-label="Увеличи количеството"
                      sx={stepperSx}
                      onClick={handleCartAmountChange(qty + 1, item)}>
                      <Add sx={{ fontSize: 16 }} />
                    </BazarButton>
                  </FlexBox>

                  <PriceStack amount={lineEUR} size="sm" />
                </FlexBox>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── footer ─────────────────────────────────────────────── */}
      {!!itemCount && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'hairline.main', p: 2.5 }}>
          <FlexBox justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>
              {strings[lang].subtotal}
            </Typography>
            <PriceStack amount={getTotalPrice()} size="md" />
          </FlexBox>

          <Divider sx={{ mb: 2.5 }} />

          <Link to={routerUrls.cart} style={{ color: 'inherit', textDecoration: 'none' }}>
            <BazarButton
              color="primary"
              variant="contained"
              sx={{ height: 44 }}
              fullWidth
              onClick={toggleSidenav}>
              {strings[lang].viewCart}
            </BazarButton>
          </Link>
        </Box>
      )}
    </Box>
  );
};

MiniCart.propTypes = {
  toggleSidenav: PropTypes.func.isRequired
};

export default MiniCart;
